import { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import './App.css';
import logo from './logo.png';

// ── Static data ─────────────────────────────────────────────
const companyName = "YEAB TECH Networking And Trading PLC";
const generalManager = "Yabsira Tamiru";
const deputyManager = "Tamiru Melese";
const contactPhone1 = "+251 930 00 02 93";
const contactPhone2 = "+251 911 21 70 61";
const contactEmail = "yeabtech1@gmail.com";
const contactEmail2 = "yabsira@yeabtech.com";

const servicesList = [
  { icon: "⚡", title: "Network Installation & Troubleshooting", description: "Expertise in network design, installation, and maintenance of data systems." },
  { icon: "💻", title: "Software Development", description: "Custom web and application development tailored to your business needs." },
  { icon: "🔧", title: "IT Support", description: "Reliable technical support to keep your operations running smoothly." },
  { icon: "🏗️", title: "Building Construction", description: "High‑quality construction services with attention to detail and safety." },
  { icon: "🚚", title: "Construction Material Supply", description: "Trusted supplier of construction materials for projects of all sizes." },
];

const clientsList = [
  { name: "TAYAM Engineering and Commerce PLC", logo: process.env.PUBLIC_URL + "/Tayam.png", description: "Network infrastructure for a major commercial building." },
  { name: "Value Engineering PLC", logo: process.env.PUBLIC_URL + "/Value.png", description: "IT support & structured cabling for their headquarters." },
  { name: "LYX Engineering PLC", logo: process.env.PUBLIC_URL + "/lyx.jpg", description: "Custom software solution for project management." },
];

// ── Helper: smooth scroll with header offset ─────────────────
const useScrollToSection = (headerRef) => {
  const scrollTo = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (!section) return;
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const top = section.offsetTop - headerHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  };
  return scrollTo;
};

// ── Main App ──────────────────────────────────────────────────
function App() {
  const headerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero-section');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);

  // Contact form state
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const scrollToSection = useScrollToSection(headerRef);

  // ── Scroll spy and sticky header ──────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      // Sticky header
      setScrolled(window.scrollY > 50);

      // Active section detection
      const sections = ['hero-section', 'about', 'services', 'clients', 'contact'];
      const headerHeight = headerRef.current?.offsetHeight || 0;
      let current = 'hero-section';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.offsetTop - headerHeight - 100 <= window.scrollY) {
          current = id;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Contact form handlers ─────────────────────────────────
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!formData.name || !formData.email || !formData.message) {
    setFormError('Please fill in all fields.');
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    setFormError('Please enter a valid email address.');
    return;
  }

  try {
    const response = await fetch('https://formspree.io/f/mzdoqqnr', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    if (response.ok) {
      setFormSubmitted(true);
      setFormError('');
    } else {
      setFormError('Something went wrong. Please try again.');
    }
  } catch (error) {
    setFormError('Network error. Please try again later.');
  }
};

  // ── Intersection observers for reveal animations ──────────
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [aboutRef, aboutInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [clientsRef, clientsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [contactRef, contactInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  // ── Lazy load the map ─────────────────────────────────────
  const [mapRef, mapInView] = useInView({ triggerOnce: true, rootMargin: '200px' });

  const [formErrors, setFormErrors] = useState({});

const validateField = (field) => {
  const value = formData[field];
  let error = '';
  if (!value.trim()) {
    error = 'This field is required.';
  } else if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    error = 'Enter a valid email.';
  }
  setFormErrors(prev => ({ ...prev, [field]: error }));
};

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, [mobileMenuOpen]);

  return (
    <div className="App">
      <header ref={headerRef} className={scrolled ? 'scrolled' : ''}>
        <div className="container header-container">
          <div className="logo" onClick={() => scrollToSection('hero-section')}>
            <img src={logo} alt="YEAB TECH Logo" width="80" height="80" loading="lazy" decoding="async"  />
          </div>

          <button
            className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <nav className={mobileMenuOpen ? 'mobile-open' : ''}>
            <a
              href="#about"
              className={activeSection === 'about' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('about'); setMobileMenuOpen(false); }}
            >About Us</a>
            <a
              href="#services"
              className={activeSection === 'services' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('services'); setMobileMenuOpen(false); }}
            >Services</a>
            <a
              href="#clients"
              className={activeSection === 'clients' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('clients'); setMobileMenuOpen(false); }}
            >Clients</a>
            <a
              href="#contact"
              className={activeSection === 'contact' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollToSection('contact'); setMobileMenuOpen(false); }}
            >Contact</a>
          </nav>

          {/* Backdrop for mobile menu */}
          {mobileMenuOpen && <div className="nav-backdrop" onClick={() => setMobileMenuOpen(false)} />}
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero-section" ref={heroRef} className={`section-animate ${heroInView ? 'is-visible' : ''}`}>
        <div className="container">
          <h1>Welcome to <span className="green-accent">{companyName}</span></h1>
          <p>
            Your trusted partner in technological advancement and infrastructure development in Ethiopia.
            We specialize in Network Installation, IT Support, and Software Development.
          </p>
          <button className="hero-button" onClick={() => scrollToSection('about')}>
             Discover More <span className="btn-arrow">→</span>
          </button>
        </div>
        <div className="scroll-indicator"><span></span></div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className={`section-animate ${aboutInView ? 'is-visible' : ''}`}>
        <div className="container">
          <h2>About Us</h2>
          <p>
            {companyName} is a dynamic and innovative company based in Addis Ababa, Ethiopia.
            We are dedicated to providing exceptional services in IT projects. With a focus on
            delivering high-quality solutions tailored to our clients' needs, we strive to be a
            trusted partner in driving technological advancement and infrastructure development.
            Our firm is licensed for Networking of data systems, software development, and construction contracting.
          </p>
          <p>
            Our operations are led by General Manager <strong>{generalManager}</strong>, Founder of the company,
            with a BSc in Computer Science and previous experience in Network Installation and Troubleshooting
            at Infomed Technologies PLC.
          </p>
          <p>
            Our Deputy Manager, co‑founder <strong>{deputyManager}</strong>, brings over 30 years of experience
            in the construction industry, with a background in construction management and technology.
          </p>
        </div>
      </section>

      {/* Services Section – staggered card animation */}
      <section id="services" ref={servicesRef} className={`section-animate ${servicesInView ? 'is-visible' : ''}`}>
        <div className="container">
          <h2>Our Services</h2>
          <div className="services-grid">
            {servicesList.map((service, index) => (
              <div
                key={index}
                className={`service-card ${servicesInView ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <span className="service-icon">{service.icon}</span>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" ref={clientsRef} className={`section-animate ${clientsInView ? 'is-visible' : ''}`}>
        <div className="container">
          <h2>Our Clients</h2>
          <div className="clients-grid">
            {clientsList.map((client, index) => (
              <div key={index} className="client-card">
                <img src={client.logo} alt={`${client.name} logo`} />
                <h4>{client.name}</h4>
                <p>{client.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" ref={contactRef} className={`section-animate ${contactInView ? 'is-visible' : ''}`}>
        <div className="container">
          <h2>Contact Us</h2>
          <div className="contact-wrapper">
            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <div>
                  <strong>Phone</strong><br />
                  {contactPhone1}<br />
                  {contactPhone2}
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <div>
                  <strong>Email</strong><br />
                  <a href={`mailto:${contactEmail}`}>{contactEmail}</a><br />
                  <a href={`mailto:${contactEmail2}`}>{contactEmail2}</a>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <div>
                  <strong>Address</strong><br />
                  Gabon St, Family Building | Meskel Flower
                </div>
              </div>
            </div>

            <div className="contact-form">
              {formSubmitted ? (
                <div className="form-success">
                  <h3>Thank you!</h3>
                  <p>Your message has been received. We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h3>Send a Message</h3>
                  {formError && <p className="form-error">{formError}</p>}
                  <div className="form-group">
                   <label htmlFor="name">Name</label>
                   <input 
                     type="text" id="name" name="name" 
                     value={formData.name} 
                     onChange={handleChange} 
                     onBlur={() => validateField('name')}
                     className={formErrors.name ? 'input-error' : ''}
                     required 
                      />
                      {formErrors.name && <span className="field-error">{formErrors.name}</span>}
                     </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea id="message" name="message" rows="5" value={formData.message} onChange={handleChange} required></textarea>
                  </div>
                  <button type="submit" className="submit-btn">Send Message</button>
                </form>
              )}
            </div>
          </div>

          {/* Map – lazy loaded */}
          <div ref={mapRef} className="map-container">
            {mapInView &&  !mapFailed ? (
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d246.29803444040704!2d38.7661349486982!3d8.993432635933102!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b857df4ae88e5%3A0x1bd5e96fee781e52!2sFamily%20Building%20%7C%20Meskel%20Flower!5e0!3m2!1sen!2set!4v1752518327971!5m2!1sen!2set"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Company Location"
              />
            ):(
              <div className="map-failed">
                <p>📍 Gabon St, Family Building | Meskel Flower</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container footer-grid">
          <div className="footer-brand">
            <img src={logo} alt="YEAB TECH Logo" className="footer-logo" />
            <p>{companyName}</p>
            <p>Technology & Infrastructure Partners</p>
          </div>
          <div className="footer-links">
            <h4>Quick Links</h4>
            <button onClick={() => scrollToSection('about')}>About</button>
            <button onClick={() => scrollToSection('services')}>Services</button>
            <button onClick={() => scrollToSection('clients')}>Clients</button>
            <button onClick={() => scrollToSection('contact')}>Contact</button>
          </div>
          <div className="footer-contact">
            <h4>Get in Touch</h4>
            <p>{contactPhone1}</p>
            <p>{contactEmail}</p>
            <p>Addis Ababa, Ethiopia</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
        </div>
      </footer>
      {/* Back‑to‑Top Button */}
       <button 
        className={`back-to-top ${scrolled ? 'visible' : ''}`} 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>
</button>
    </div>
  );
}

export default App;